import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { jwtDecode } from 'jwt-decode';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public csrfToken$ = new BehaviorSubject(undefined);
    private fetchNewCsrfToken$: Observable<{ token: string }>;

    private httpClient: HttpClient;

    constructor(handler: HttpBackend) {
        this.httpClient = new HttpClient(handler);
    }

    public hasValidCsrfToken(): boolean {
        const token = this.getCsrfToken();
        return token && this.isCsrfTokenValid(token);
    }

    public getCsrfToken(): string {
        return this.csrfToken$.value;
    }

    init() {
        this.fetchNewCsrfToken$ = this.getCsrfTokenRequest().pipe(
            tap({
                next: ({ token }) => this.csrfToken$.next(token),
                error: () => this.redirectToLoginScreen(),
            }),
            share()
        );

        const token = this.getCsrfTokenFromUrl();

        if (token && this.isCsrfTokenValid(token)) {
            this.csrfToken$.next(token);
        } else {
            this.fetchNewCsrfToken$.subscribe();
        }
    }

    fetchNewCsrfToken() {
        return this.fetchNewCsrfToken$;
    }

    public getCsrfTokenRequest() {
        return this.httpClient.post<{ token: string }>(
            `${environment.auth.baseUrl}/auth/v1/saml/csrf`,
            {
                appId: environment.auth.appId,
            },
            {
                withCredentials: true,
            }
        );
    }

    private getCsrfTokenFromUrl(): string | undefined {
        const hash = window.location.hash;
        if (hash && hash.startsWith('#token=')) {
            const value = new HttpParams({ fromString: hash }).get('#token');
            window.location.hash = ''; // remove token form url
            return value;
        }
    }

    private redirectToLoginScreen() {
        const redirectTo = `${environment.auth.baseUrl}/saml/sso/request?appId=${
            environment.auth.appId
        }&redirectUrl=${encodeURIComponent(window.location.href)}`;
        window.location.href = redirectTo;
    }

    private isCsrfTokenValid(token: string) {
        try {
            return !this.isCSRFTokenExpired(token);
        } catch (_) {
            return false;
        }
    }

    isCSRFTokenExpired(token: string): boolean {
        const date = this.getTokenExpirationDate(token);
        if (!date) {
            return false;
        }
        return !(date.valueOf() > new Date().valueOf());
    }

    getTokenExpirationDate(token: string): Date {
        const decoded = jwtDecode<{ exp: number }>(token);

        if (decoded.exp === undefined) {
            return null;
        }

        const date = new Date(0);
        date.setUTCSeconds(decoded.exp);
        return date;
    }
}
